<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Cambiar contraseña"
    :breakpoints="{ '600px': '75vw', ' 400px': '100vw' }"
  >
    <form id="login-form" @submit.prevent="update">
      <label for="oldPassword">Contraseña actual <span>*</span></label>
      <InputText
        v-model="oldPassword"
        autocomplete="current-password"
        type="password"
        id="oldPassword"
        :feedback="false"
      />
      <span class="error-message">
        {{ oldPasswordError }}
      </span>
      <br />

      <label for="password">Contraseña nueva<span>*</span></label>
      <InputText
        autocomplete="new-password"
        v-model="newPassword"
        type="password"
        id="password"
        :feedback="false"
      />
      <span class="error-message">
        {{ newPasswordError }}
      </span>
      <br />

      <label for="confirmPassword"
        >Confirmar contraseña nueva<span>*</span></label
      >
      <InputText
        v-model="confirmPassword"
        autocomplete="new-password"
        type="password"
        id="confirmPassword"
        :feedback="false"
      />
      <span class="error-message">
        {{ confirmError }}
      </span>
      <br />

      <Button
        type="submit"
        :class="isLoading ? 'p-disabled loading' : ''"
        :disabled="!isValidToUptade"
      >
        <span v-if="isLoading">
          <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
        </span>
        <span v-else>Actualizar</span>
      </Button>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { ObservableService } from '@/shared/services'
  import { onMounted, ref, watch, computed } from 'vue'
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import logo from '../../../assets/logo_new_res.png'
  import { useUserStore } from '@/store/user'
  import { useRouter } from 'vue-router'
  import useNotifications from '@/store/notification'

  const notificationStore = useNotifications()

  const oldPaswordSchema = yup
    .string()
    .required('La nueva contraseña es requerida')

  const newPasswordSchema = yup
    .string()
    .required('La nueva contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .test(
      'password-complexity',
      'La contraseña debe contener al menos una letra mayúscula y un número',
      (value) => {
        // La expresión regular para validar al menos una letra mayúscula y un número
        const regex = /^(?=.*[A-Z])(?=.*\d)/
        return regex.test(value)
      }
    )

  const confirmPasswordSchema = yup
    .string()
    .required('La confirmación de la nueva contraseña es requerida')
    .test(
      'passwords-match',
      'Las contraseñas deben coincidir',
      function (value) {
        return value === newPassword.value
      }
    )
    .min(8, 'La contraseña debe tener al menos 8 caracteres')

  const {
    value: oldPassword,
    errorMessage: oldPasswordError,
    resetField: passReset,
  } = useField<string>('oldPassword', oldPaswordSchema)
  const {
    value: newPassword,
    errorMessage: newPasswordError,
    resetField: newPasswordReset,
  } = useField<string>('newPassword', newPasswordSchema)
  const {
    value: confirmPassword,
    errorMessage: confirmError,
    resetField: confirmPassReset,
  } = useField<string>('confirmPassword', confirmPasswordSchema)

  const router = useRouter()
  const store = useUserStore()
  const isLoading = ref(false)
  const isSubmitted = ref(false)

  const update = async () => {
    isSubmitted.value = true
    isLoading.value = true
    const response = await store.updatePassword(
      oldPassword.value,
      newPassword.value
    )
    isLoading.value = false

    if (response) {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `¡Contraseña actualizada con éxito!`,
      })

      isVisible.value = false

      // Clear the field values
      passReset()
      newPasswordReset()
      confirmPassReset()
    }

    passReset()
    newPasswordReset()
    confirmPassReset()

    // Reset form fields without showing errors
  }

  const isValidToUptade = computed(
    () =>
      oldPassword.value &&
      confirmPassword.value &&
      newPassword.value &&
      confirmPassword.value === newPassword.value
  )

  const observableService = ObservableService.getInstance()
  const isVisible = ref(false)

  watch(isVisible, (_, newValue) => {
    if (newValue) {
      observableService.emitValue('update-password', !newValue)
    }
  })

  const clear = () => {}

  onMounted(() => {
    setTimeout(() => {
      observableService.getObservable('update-password')?.subscribe((value) => {
        isVisible.value = value
      })
    }, 500)
  })
</script>

<style scoped>
  .container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    height: auto;
    background-color: #f2f2f2;
    background-image: url('../../../assets/LinePattern_login.svg');
  }

  .login-card {
    width: 25em;
    min-height: 4rem;
    padding: 2rem;
  }

  .logo {
    width: 100%;
    object-fit: contain;
    height: 80px;
  }

  #login-form {
    display: flex;
    flex-flow: column wrap;
    text-align: left;
  }

  Button {
    display: block;
  }

  label {
    padding-bottom: 5px;
  }

  .error-message {
    padding-top: 5px;
    color: rgb(234, 68, 68);
    font-size: 12px;
  }
</style>
